/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2025
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./GridDataProcessor'
], function (
	GridDataProcessor
) {
	/**
	 * Table in release 11.2.0 is using V1 Query to retrieve data.
	 * This class must be revisited when Table get switch to use V2 Query to retrieve data
	 * The purpose of this class is to filter and remove all columns that have null values from the query result set
	 * and to remove all columns that have null values from column slot mappings
	 * Essentially this class remove all columns that have null values as these were not mapped in column slot
	*/
	return class GridNullSuppressionProcessor {
		constructor (options) {
			this._queryResult = options.queryResult;
			this._isSuppressionOn = options.isSuppressionOn;
			this._columnSlot = options.columnSlot;
			this._heatSlot = options.heatSlot;
		}

		getDataPointValueList () {
			return this._queryResult.getDataPointValueList();
		}

		getResultItemList () {
			return this._resultItemListWithSuppression || this._queryResult.getResultItemList();
		}

		getRowCount () {
			return this._queryResult.getRowCount();
		}

		getValue (rowId, colIdx) {
			return this._queryResult.getValue(rowId, this._getActualColumnIdxToQueryResultSet(colIdx));
		}

		getSlotDataItemList (slot) {
			return slot.getId() === 'heat' ? this._heatSlot.getDataItemList() : (this._columSlotDataItemListWithSuppression || this._columnSlot.getDataItemList());
		}

		processNullSuppression () {
			if (!this._isSuppressionOn) {
				return;
			}
			let colIdx, rowIdx, value, useValue;
			const suppressedIds = [];
			const nonSuppressedColumnIndex = [];
			const resultItemList = this._queryResult.getResultItemList();
			const dataRowCount = this._queryResult.getRowCount();
			nonSuppressedColumnIndex.length = resultItemList.length;
			// Necessary to loop through all rows of data.
			// if all rows for a column have null values then that column is suppressed and will not get render on the table
			// If column have mixture of values and null then that column get render in the table
			for (rowIdx = 0; rowIdx < dataRowCount; rowIdx++) {
				for (colIdx = 0; colIdx < resultItemList.length;  colIdx++) {
					// Once the column has a value then don't bother checking this again
					if (!nonSuppressedColumnIndex[colIdx]) {
						const index = suppressedIds.indexOf(resultItemList[colIdx].getId());
						value = GridDataProcessor.getCellValue(this._queryResult, rowIdx, colIdx);
						if (!GridDataProcessor.isUndefined(value)) {
							useValue = GridDataProcessor.getUseValue(value);
							const isSuppressed = (useValue === null && value.deco && value.deco.state === 'NULL');
							if (isSuppressed) {
								if (index === -1) {
									// add the suppressed id
									suppressedIds.push(resultItemList[colIdx].getId());
								}
							} else {
								// The column is valid so marke it as valid
								nonSuppressedColumnIndex[colIdx] = true;
								if (index !== -1) {
									// Remove the id in the list if it was added before.
									// For example, row 1 column has null then row 2 the column has value
									// in this case remove the suppressed id added by row 1
									suppressedIds.splice(index, 1);
								}
							}
						}
					}
				}
			}

			// Have suppress columns so remove them
			if (suppressedIds.length > 0) {
				// Remove all suppressed items
				this._resultItemListWithSuppression = resultItemList.filter(resultItem => {
					const suppressedItem = suppressedIds.find(id => {
						return resultItem.getId() === id;
					});
					return suppressedItem ? false : true;
				});

				let dataItemList = this._columnSlot && this._columnSlot.getDataItemList() || [];
				this._columSlotDataItemListWithSuppression = dataItemList.filter(resultItem => {
					const suppressedItem = suppressedIds.find(id => {
						return resultItem.getId() === id;
					});
					return suppressedItem ? false : true;
				});

				// Now find real index to data
				let currentIndex = 0;
				this._colIdxWithSuppression = [];
				for (colIdx = 0; colIdx < this._resultItemListWithSuppression.length; colIdx++) {
					const id = this._resultItemListWithSuppression[colIdx].getId();
					if (currentIndex < resultItemList.length) {
						for (let index = currentIndex; index < resultItemList.length; index++) {
							if (resultItemList[index].getId() === id) {
								this._colIdxWithSuppression.push(index);
								++currentIndex;
								break;
							}
						}
					}
				}
			}
		}

		_getActualColumnIdxToQueryResultSet (colIdx) {
			return (this._isSuppressionOn && this._colIdxWithSuppression && this._colIdxWithSuppression[colIdx] !== undefined) ? this._colIdxWithSuppression[colIdx] : colIdx;
		}
	};
});
